<script setup lang="ts">
import { computed, ref } from 'vue'
import { NButton, NCheckbox, NInput, NSelect } from 'naive-ui'
import type { SelectGroupOption } from 'naive-ui'
import type { AgentPrompt } from '../stores/agent_prompts'

// The /agent page's message form, split out of AgentView so that file
// stays under the 300-line cap — this owns the repo picker, the
// canned-prompt picker, the textarea, and the send/cancel buttons.
// `repo`/`draft` are v-models so the parent still owns the actual state
// (needed for `send()`'s API call and `newSession()`'s reset). Reuses the
// Prompt Library's `AgentPrompt` type rather than a component-local one —
// `<script setup>` can't expose named type exports to plain .ts importers
// (only other .vue SFCs), so a new local type would be unusable from this
// component's own spec file.
const props = defineProps<{
  repo: string
  draft: string
  repoOptions: Array<{ label: string; value: string }>
  prompts: AgentPrompt[]
  isNewSession: boolean
  canSend: boolean
  sending: boolean
  activeRunning: boolean
  agentRunning: boolean
  homeChannelName?: string | null
  postToChannel?: boolean
}>()

const emit = defineEmits<{
  'update:repo': [value: string]
  'update:draft': [value: string]
  'update:postToChannel': [value: boolean]
  send: []
  cancel: []
}>()

const generalPrompts = computed(() => props.prompts.filter((p) => p.repo_slug === null))
const repoPrompts = computed(() => props.prompts.filter((p) => p.repo_slug === props.repo))
const hasPrompts = computed(() => generalPrompts.value.length > 0 || repoPrompts.value.length > 0)

// Grouped dropdown — chips felt cluttered once a scope had more than a
// couple of prompts. Options key on prompt id, not label — labels aren't
// guaranteed unique.
const promptOptions = computed<SelectGroupOption[]>(() => {
  const groups: SelectGroupOption[] = []
  if (generalPrompts.value.length > 0) {
    groups.push({
      type: 'group',
      key: 'general',
      label: 'General',
      children: generalPrompts.value.map((p) => ({ label: p.label, value: p.id })),
    })
  }
  if (repoPrompts.value.length > 0) {
    groups.push({
      type: 'group',
      key: props.repo,
      label: props.repo,
      children: repoPrompts.value.map((p) => ({ label: p.label, value: p.id })),
    })
  }
  return groups
})

// Held locally rather than as a v-model into the parent: picking a prompt
// is a one-shot action (replace the draft), not persistent selection state,
// so the dropdown resets to its placeholder after every pick.
const selectedPromptId = ref<number | null>(null)

// Replaces the draft outright (no cursor-insertion) — the field stays a
// normal editable textarea afterward, per the parent PRD's decision.
function onPromptSelect(id: number) {
  selectedPromptId.value = null
  const prompt = props.prompts.find((p) => p.id === id)
  if (prompt) emit('update:draft', prompt.text)
}

function onEnter(event: KeyboardEvent) {
  // Don't submit mid-IME-composition (CJK/accented input) — Enter there is
  // committing a character, not sending the message.
  if (event.isComposing) return
  emit('send')
}
</script>

<template>
  <form class="flex flex-col gap-2" data-testid="agent-form" @submit.prevent="emit('send')">
    <NSelect
      v-if="isNewSession"
      :value="repo"
      :options="repoOptions"
      placeholder="Repo (defaults to superbrain)"
      clearable
      data-testid="agent-repo"
      @update:value="emit('update:repo', $event)"
    />
    <NSelect
      v-if="isNewSession && hasPrompts"
      :value="selectedPromptId"
      :options="promptOptions"
      placeholder="Load a canned prompt…"
      data-testid="agent-prompt-picker"
      @update:value="onPromptSelect"
    />
    <div class="flex gap-2 min-w-0">
      <NInput
        :value="draft"
        class="flex-1 min-w-0"
        type="textarea"
        :autosize="{ minRows: 1, maxRows: 6 }"
        placeholder="Message the agent…"
        data-testid="agent-input"
        @update:value="emit('update:draft', $event)"
        @keydown.enter.exact.prevent="onEnter"
      />
      <NButton v-if="activeRunning" type="error" data-testid="agent-cancel" @click="emit('cancel')"> Cancel </NButton>
      <NButton type="primary" :disabled="!canSend" :loading="sending" data-testid="agent-send" @click="emit('send')">
        Send
      </NButton>
    </div>
    <NCheckbox
      v-if="homeChannelName && !isNewSession"
      :checked="postToChannel ?? false"
      data-testid="agent-post-to-channel"
      @update:checked="emit('update:postToChannel', $event)"
    >
      Also post to #{{ homeChannelName }}
    </NCheckbox>
    <p class="text-xs text-white/40 m-0" data-testid="agent-hint">
      <template v-if="agentRunning">Agent still working — your message will send once it's done.</template>
      <template v-else>Enter to send · Shift+Enter for a newline.</template>
    </p>
  </form>
</template>
